import styled from '@emotion/styled'

import {Divider} from '@vio/atlas/Divider'
import {EndContent, NavBarElement, StartContent} from '@vio/atlas/NavBar'
import {cssTheme} from '@vio/atlas/themes'
import {CUSTOM_HEADER_HEIGHT_MOBILE} from '@vio/shared/src/whiteLabel/CustomHeader/KiwiHeader/constants'

const ICON_SIZE = 44
const TRANSITION_DURATION = '0.3s'

export const HeaderElement = styled.header<{heroImage?: string}>`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${cssTheme.colors.background.neutral.primary};
  text-align: center;

  /*
   * The NavBar is a direct child now (no wrapper). It's lifted above the
   * background pseudos (the WL "bubble" :before and the hero :after).
   */
  ${NavBarElement} {
    position: relative;
    z-index: 1;
  }

  ${StartContent} {
    margin-left: 0;
  }

  /* Custom-header brands need the gap between their header and the NavBar. */
  &.--has-custom-header ${NavBarElement} {
    margin-top: ${cssTheme.layout.spacing.s500};
  }

  &:before {
    content: '';
    background-color: ${cssTheme.colors.srp.topBar.background};
    display: block;
    width: calc(100vw * 1.2);
    height: 152px;
    position: absolute;
    top: -45px;
    border-radius: 50%;
    z-index: 0;
    left: -11%;
    transition:
      opacity 0.1s,
      top ${TRANSITION_DURATION},
      height ${TRANSITION_DURATION};
  }

  &.--branded-top-bar-bubble {
    &:before {
      background-color: ${cssTheme.colors.background.brand.tertiary};
    }
  }

  &.--compact {
    ${NavBarElement} {
      --nav-bar-primary-row-min-height: 12px;
    }
    ${StartContent}, ${EndContent} {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition:
        opacity ${TRANSITION_DURATION},
        visibility ${TRANSITION_DURATION};
    }
    &:before {
      height: 77px;
      top: -30px;
    }
  }
  &.--hide-background {
    &:before {
      height: 0;
      opacity: 0;
    }
  }

  &.--no-top-bar-bubble {
    &:before {
      background-color: transparent;
      border-radius: 0;
    }
  }

  /*
   * Collapse the custom header on scroll (compact). Each brand header exposes
   * its own (responsive) height as --custom-header-height, so animating
   * max-height from that exact value down to 0 stays linear at every
   * breakpoint. Covers Kiwi/Finn (<header>) and HolidayPirates (#hp-header).
   */
  &.--has-custom-header > header,
  &.--has-custom-header > #hp-header {
    overflow: hidden;
    max-height: var(--custom-header-height);
    transition:
      opacity ${TRANSITION_DURATION},
      max-height ${TRANSITION_DURATION};
  }

  &.--has-custom-header.--compact > header,
  &.--has-custom-header.--compact > #hp-header {
    opacity: 0;
    max-height: 0;
  }

  /*
   * WL hero background (currently kiwi). Lives on :after because :before is the
   * "bubble". Offset from the top by the custom header's height so it starts
   * below the (transparent) custom header rather than bleeding behind its logo,
   * mirroring where the old NavBarWrapper sat. Fades out in the compact state.
   */
  ${({heroImage}) =>
    heroImage &&
    `
    &:after {
      content: '';
      position: absolute;
      top: ${CUSTOM_HEADER_HEIGHT_MOBILE}px;
      right: 0;
      bottom: 0;
      width: 390px;
      z-index: 0;
      background:
        url(${heroImage}) right top no-repeat,
        ${cssTheme.colors.homepage.hero.background};
      background-size: 390px auto, cover;
      transition: opacity ${TRANSITION_DURATION};
    }

    &.--compact:after {
      opacity: 0;
    }
  `}
`

export const StyledLogo = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: ${ICON_SIZE}px;
  width: auto;
  margin: 0;
`

export const EndContentWrapper = styled.div`
  min-width: ${ICON_SIZE}px;
`

export const VerticalDivider = styled(Divider)`
  height: 20px;
  align-self: center;
`
